import clsx from 'clsx';
import React from 'react';

type SegmentedButton = {
  value: string;
  label: React.ReactNode;
  icon?: React.ReactNode;
  required?: boolean;
};

type SegmentedButtonsProps = {
  value: string;
  onValueChange: (value: string) => void;
  buttons: SegmentedButton[];
  className?: string;
  variant?: 'contained' | 'outlined' | 'text';
  color?: 'primary' | 'secondary' | 'error' | 'default';
  disabled?: boolean;
};

const variantClasses = {
  contained: {
    primary:
      'bg-primary text-neutral-0 hover:bg-primary/90 active:bg-primary/80',
    secondary:
      'bg-neutral-400 text-neutral-0 hover:bg-neutral-500 active:bg-neutral-600',
    error: 'bg-error text-neutral-0 hover:bg-error-500 active:bg-error-600',
    default:
      'bg-neutral-400 text-neutral-0 hover:bg-neutral-500 active:bg-neutral-600',
  },
  outlined: {
    primary:
      'border border-primary text-primary hover:bg-neutral-50 active:bg-neutral-100',
    secondary:
      'border-2 border-neutral-400 text-neutral-400 hover:bg-neutral-50 active:bg-neutral-100',
    error:
      'border-2 border-error text-error hover:bg-error-500 active:bg-error-600',
    default:
      'border-2 border-neutral-400 text-neutral-400 hover:bg-neutral-50 active:bg-neutral-100',
  },
  text: {
    primary: 'text-primary hover:bg-primary/10 active:bg-primary/20',
    secondary: 'text-neutral-400 hover:bg-neutral-50 active:bg-neutral-100',
    error: 'text-error hover:bg-error-500 active:bg-error-600',
    default: 'text-neutral-400 hover:bg-neutral-50 active:bg-neutral-100',
  },
};

export const SegmentedButtons: React.FC<SegmentedButtonsProps> = ({
  value,
  onValueChange,
  buttons,
  className,
  variant = 'outlined',
  color = 'primary',
  disabled = false,
}) => {
  return (
    <div className={clsx('inline-flex rounded-lg overflow-hidden', className)}>
      {buttons.map((btn, idx) => {
        const selected = value === btn.value;
        return (
          <button
            key={btn.value}
            type="button"
            className={clsx(
              'flex items-center gap-2 px-6 py-2.5 text-base font-medium transition-colors focus:outline-none whitespace-nowrap',
              idx === 0 ? 'rounded-l-lg' : '',
              idx === buttons.length - 1 ? 'rounded-r-lg' : '',
              selected
                ? variantClasses.contained[color]
                : variantClasses[variant][color],
              !selected && variant === 'contained' ? 'opacity-80' : '',
              disabled && 'opacity-50 cursor-not-allowed',
            )}
            onClick={() => onValueChange(btn.value)}
            aria-pressed={selected}
            disabled={disabled}
          >
            {btn.icon && <span>{btn.icon}</span>}
            {btn.label}
            {btn.required && <span className="text-error">*</span>}
          </button>
        );
      })}
    </div>
  );
};
